import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Input, Button, Spin, Row, Col, Badge, Pagination, Empty, Divider, Select } from 'antd';
import { Search, Package, ShoppingCart, X, ChevronDown, FileText, Plus, Minus } from 'lucide-react';
import { useProductSelection } from '../hooks/useProductSelection';
import { useProductFetching } from '../hooks/useProductFetching';
import { useProductSearch } from '../hooks/useProductSearch';
import PopupComponent from './PopupComponent';
import ProductCard from './ProductCard';
import ShareCatalogueComponent from './ShareCatalogueComponent';
import styles from './ProductPickerPopup.module.scss';
import { useNavigate } from 'react-router-dom';
import { FaShare } from 'react-icons/fa';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

// Use your original Product type definitions
export interface Product {
  quantity: number;
  _id: string;
  name: string;
  description?: string;
  price: number;
  category?: {
    _id: string;
    name: string;
  };
  extraDetails?: {
    unit?: string;
    [key: string]: any;
  };
  images?: Array<{
    filePath: string;
    fileName: string;
  }>;
}

interface ProductWithQuantity extends Product {
  id?: string;
  quantity: number; // Can be decimal
  saleValue?: number; // Manual override for sale value
}

// ProductPickerPopup Props Interface
interface ProductPickerPopupProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProductIds?: string[]; 
  onProductsSelect: (products: ProductWithQuantity[]) => void;
  initialSelectedProducts?: ProductWithQuantity[];
  excludeProductIds?: string[];
  leadId?: string;
  leadName?: string;
  source?: 'lead' | 'grid' | 'quotation' | 'invoice'; 
}

const ProductPickerPopup: React.FC<ProductPickerPopupProps> = ({
  isOpen,
  onClose,
  onProductsSelect,
  initialSelectedProducts = [],
  excludeProductIds = [],
  leadId, 
  leadName,
  source = 'lead',
  selectedProductIds = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(16);
  const [paginationMode, setPaginationMode] = useState<'paged' | 'infinite'>('paged');
  const [isShareCatalogueOpen, setIsShareCatalogueOpen] = useState(false);
  
  const [manualTotal, setManualTotal] = useState<string>('');

  const searchInputRef = useRef<any>(null);
  const productListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Custom hooks
  const { searchTerm, debouncedSearchTerm, handleSearchChange, handleClearSearch, resetSearch } = useProductSearch();
  
const { 
  selectedProducts, 
  handleToggleProduct, 
  handleQuantityChange, 
  handleSaleValueChange,  // NEW: Add this
  calculateTotals, 
  handleConfirm 
} = useProductSelection({
  isOpen,
  initialSelectedProducts,
  onProductsSelect
});

  const { products, loading, totalProducts, handleScroll } = useProductFetching({
    isOpen,
    currentPage,
    pageSize,
    searchTerm: debouncedSearchTerm,
    paginationMode,
    excludeProductIds,
    selectedProductIds
  });

  // Reset everything when popup opens
  useEffect(() => {
  if (!isOpen) return;

  logger.log('🎯 ProductPickerPopup: Opening popup');
  resetSearch();
  setCurrentPage(1);
  
  // Clear manual total when popup opens (only for lead source)
  if (source === 'lead') {
    setManualTotal('');
  }
  
  // Focus search input
  const timerId = setTimeout(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, 100);
  
  return () => clearTimeout(timerId);
}, [isOpen, resetSearch, source]);

  // Handle pagination mode change
  const handlePaginationModeChange = useCallback((mode: 'paged' | 'infinite') => {
    logger.log('🔄 Changing pagination mode to:', mode);
    setPaginationMode(mode);
    setCurrentPage(1);
  }, []);

  // Handle infinite scroll
  const onContainerScroll = useCallback(() => {
    if (!productListRef.current) return;
    
    const nextPage = handleScroll(productListRef.current);
    if (nextPage) {
      setCurrentPage(nextPage);
    }
  }, [handleScroll]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    logger.log('📄 Changing to page:', page);
    setCurrentPage(page);
    
    // Scroll to top when changing pages
    if (productListRef.current) {
      productListRef.current.scrollTop = 0;
    }
  }, []);

  // Share catalogue handler
  const handleShareCatalogue = useCallback(() => {
    if (selectedProducts.size === 0) {
      toast.error('Please select at least one product to share');
      return;
    }
    
    logger.log('📤 Opening share catalogue dialog');
    setIsShareCatalogueOpen(true);
  }, [selectedProducts.size]);

  // Create quotation handler
  const handleCreateQuotation = useCallback(() => {
    if (selectedProducts.size === 0) {
      toast.error('Please select at least one product for the quotation');
      return;
    }
    
    if (!leadId) {
      console.error('❌ Missing lead ID for quotation');
      toast.error('Lead information missing for quotation');
      return;
    }
    
    const productsArray = Array.from(selectedProducts.values());
    logger.log('📝 Creating quotation with:', productsArray.length, 'products');
    
    navigate('/quotationlist', {
      state: {
        openCreateForm: true,
        leadForQuotation: {
          _id: leadId,
          name: leadName || 'Unknown Lead'
        },
        productsForQuotation: productsArray
      }
    });
    
    onClose();
  }, [selectedProducts, navigate, leadId, leadName, onClose]);

  // Calculate totals
  const { itemCount, subtotal } = calculateTotals();

  // Render selected products list
  const SelectedProductsList = memo(() => {
    if (selectedProducts.size === 0) return null;
    
    return (
      <div className={styles.selectedProductsSection}>
        <Divider orientation="left">
          <div className={styles.selectedHeader}>
            <span>Selected Products</span>
            <Badge count={selectedProducts.size} style={{ backgroundColor: '#1890ff' }} />
          </div>
        </Divider>
        <div className={styles.selectedProductsList}>
          {Array.from(selectedProducts.values()).map(product => (
       <div className={styles.selectedProductItem}>
  <div className={styles.selectedProductInfo}>
    <div className={styles.selectedProductName}>{product.name}</div>
    <div className={styles.selectedProductPrice}>
      ₹ {product.price.toLocaleString()}
      {product.extraDetails?.unit && <span> / {product.extraDetails.unit}</span>}
    </div>
  </div>
  <div className={styles.selectedProductQuantity}>
    <Button
      icon={<Minus size={14} />}
      onClick={(e) => {
        if (e) {
          e.stopPropagation();
          e.preventDefault();
        }
        handleQuantityChange(product._id, -1);
      }}
      disabled={product.quantity <= 1}
      size="small"
    />
    <span className={styles.quantityValue}>{product.quantity}</span>
    <Button
      icon={<Plus size={14} />}
      onClick={(e) => {
        if (e) {
          e.stopPropagation();
          e.preventDefault();
        }
        handleQuantityChange(product._id, 1);
      }}
      size="small"
    />
  </div>
  <div className={styles.selectedProductTotal}>
    ₹ {(product.price * product.quantity).toLocaleString()}
  </div>
  <Button
    type="text"
    icon={<X size={14} />}
    onClick={() => handleToggleProduct(product)}
    className={styles.selectedProductRemove}
    size="small"
  />
</div>
          ))}
        </div>
      </div>
    );
  });

  // Render product grid
  const ProductGrid = memo(() => {
    if (loading && products.length === 0) {
      return (
        <div className={styles.productPickerLoading}>
          <Spin size="large" />
          <p>Loading products...</p>
        </div>
      );
    }
    
    if (products.length === 0) {
      return (
        <Empty 
          description={
            <div>
              <p>No products found</p>
              {searchTerm && (
                <Button type="link" onClick={handleClearSearch}>
                  Clear search
                </Button>
              )}
            </div>
          }
        />
      );
    }
    
    return (
      <Row gutter={[16, 16]} className={styles.productGrid}>
        {products.map(product => {
          const isSelected = selectedProducts.has(product._id);
          const selectedProduct = selectedProducts.get(product._id);
          
          return (
            <Col xs={24} sm={12} md={8} lg={6} key={product._id}>
              <div className="card-wrapper">
                <ProductCard
                  product={product}
                  isSelected={isSelected}
                  selectedProduct={selectedProduct}
                  onToggle={handleToggleProduct}
                  onQuantityChange={handleQuantityChange}
                />
              </div>
            </Col>
          );
        })}
      </Row>
    );
  });

  // Render footer
  const renderFooter = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      padding: '16px 20px',
      backgroundColor: '#fff',
      borderTop: '1px solid #eaecf0',
      boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
      borderRadius: '0 0 8px 8px'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: '180px',
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'baseline',
          color: '#64748b',
          fontSize: '14px',
          marginBottom: '4px'
        }}>
          <span>Selected Items:</span>
          <span style={{ fontWeight: 600, color: '#334155' }}>
            {selectedProducts.size} products ({itemCount} items)
          </span>
        </div>
  <div style={{
  display: 'flex',
  gap: '8px',
  alignItems: 'baseline',
  fontSize: '14px',
  color: '#64748b'
}}>
  <span>Sale Value:</span>
  {source === 'lead' ? (
    <Input
      value={manualTotal || ''}
      onChange={(e) => setManualTotal(e.target.value)}
      placeholder={`₹${subtotal.toLocaleString()}`}
      prefix="₹"
      style={{ 
        width: '150px',
        fontSize: '18px',
        fontWeight: 700
      }}
    />
  ) : (
    <span style={{
      fontWeight: 700,
      fontSize: '20px',
      color: '#6236FF',
      letterSpacing: '-0.02em'
    }}>
      ₹ {subtotal.toLocaleString()}
    </span>
  )}
</div>
      </div>
      <div style={{
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'flex-end'
      }}>
        <Button 
          onClick={onClose} 
          size="large"
          style={{ minWidth: '80px' }}
        >
          Cancel
        </Button>
        
        {(source === 'lead' || source === 'grid') && (
  <Button 
    icon={<FaShare />}
    onClick={handleShareCatalogue}
    disabled={selectedProducts.size === 0}
    size="large"
    style={{ minWidth: '100px' }}
  >
    Share Catalouge
  </Button>
)}
        
        {(source === 'lead' || source === 'grid') && leadId && (
          <Button 
            icon={<FileText size={16} />}
            onClick={handleCreateQuotation}
            disabled={selectedProducts.size === 0}
            size="large"
            style={{ 
              minWidth: '140px',
              backgroundColor: '#8B5CF6',
              color: 'white'
            }}
          >
            Create Quotation
          </Button>
        )}
        
       <Button 
  type="primary" 
  onClick={() => handleConfirm(manualTotal, source)}
  disabled={selectedProducts.size === 0}
  size="large"
  style={{ minWidth: '140px' }}
>
  <ShoppingCart size={16} style={{ marginRight: '4px' }} />
  Add Products
</Button>
      </div>
    </div>
  );

  return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Select Products"
      size="lg"
      footer={renderFooter()}
      
    >
      <div className={styles.productPickerContainer}>
        <div className={styles.productPickerLayout}>
          <div className={styles.productCatalogSection}>
            {/* Search Bar */}
            <div className={styles.productPickerSearch}>
              <Input
                ref={searchInputRef}
                placeholder="Search products by name or description..."
                prefix={<Search size={16} />}
                value={searchTerm}
                onChange={handleSearchChange}
                className={styles.searchInput}
                suffix={
                  searchTerm ? (
                    <Button 
                      type="text" 
                      icon={<X size={16} />} 
                      onClick={handleClearSearch}
                      className={styles.clearButton}
                    />
                  ) : null
                }
              />
            </div>
  
            {/* Product Grid */}
            <div 
              className={styles.productPickerContent} 
              ref={productListRef}
              onScroll={paginationMode === 'infinite' ? onContainerScroll : undefined}
            >
              <ProductGrid />
              
              {/* Show loading indicator for infinite scroll */}
              {paginationMode === 'infinite' && loading && currentPage > 1 && (
                <div className={styles.infiniteLoader}>
                  <Spin size="small" />
                  <span>Loading more products...</span>
                </div>
              )}
            </div>
  
            {/* Pagination controls */}
            <div className={styles.productPickerPagination}>
              <div className={styles.paginationOptions}>
                <Select
                  value={pageSize}
                  onChange={(value) => {
                    setPageSize(value);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 16, label: '16 / page' },
                    { value: 32, label: '32 / page' },
                    { value: 48, label: '48 / page' },
                  ]}
                  size="small"
                  dropdownMatchSelectWidth={false}
                  suffixIcon={<ChevronDown size={14} />}
                />
              </div>
              
              <Pagination
                current={currentPage}
                total={totalProducts}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper={false}
                className={styles.paginationControls}
                size="small"
              />
            </div>
          </div>
          
          {/* Selected Products Panel */}
          <div className={styles.selectedProductsPanel}>
            {selectedProducts.size > 0 ? (
              <SelectedProductsList />
            ) : (
              <div className={styles.emptySelection}>
                <Empty 
                  image={<ShoppingCart size={64} style={{ color: '#d9d9d9' }} />}
                  description="No products selected yet"
                />
                <p className={styles.helpText}>Click on products to select them</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <ShareCatalogueComponent
        isOpen={isShareCatalogueOpen}
        onClose={() => setIsShareCatalogueOpen(false)}
        productIds={Array.from(selectedProducts.keys())}
        leadId={leadId} // Pass leadId here
        leadName={leadName} // Also pass leadName for context
      />
    </PopupComponent>
  );
};

// Memoized export with custom comparison
export default React.memo(ProductPickerPopup, (prevProps, nextProps) => {
  // Always re-render when open state changes
  if (prevProps.isOpen !== nextProps.isOpen) return false;
  
  // Deep compare initialSelectedProducts
  const prevProductsKey = JSON.stringify(
    (prevProps.initialSelectedProducts || []).map(p => `${p._id}:${p.quantity}`)
  );
  const nextProductsKey = JSON.stringify(
    (nextProps.initialSelectedProducts || []).map(p => `${p._id}:${p.quantity}`)
  );
  
  // Re-render if initialSelectedProducts has changed
  if (prevProductsKey !== nextProductsKey) return false;
  
  // Don't re-render otherwise
  return true;
});